import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["isCrossDomain"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo, useRef } from 'react';
import { themeObjectToString } from '@atlaskit/tokens';
import { EMBEDDED_CONFLUENCE_MODE, EMBEDDED_CONFLUENCE_CONTENT_TYPE } from './index';
import { getAllowedFeatures } from '../features';
import { useIntl } from 'react-intl-next';
export var themeStateObjectToQueryString = function themeStateObjectToQueryString(themeState) {
  var themeString = themeObjectToString(themeState);
  return encodeURIComponent(themeString);
};
export var useUrl = function useUrl(_ref) {
  var allowedFeatures = _ref.allowedFeatures,
    contentId = _ref.contentId,
    contentType = _ref.contentType,
    contentConfiguration = _ref.contentConfiguration,
    draftShareId = _ref.draftShareId,
    hash = _ref.hash,
    hostname = _ref.hostname,
    mode = _ref.mode,
    parentProduct = _ref.parentProduct,
    parentProductContentContainerId = _ref.parentProductContentContainerId,
    parentProductContentId = _ref.parentProductContentId,
    protocol = _ref.protocol,
    spaceKey = _ref.spaceKey,
    template = _ref.template,
    themeState = _ref.themeState,
    embeddedConfluenceSource = _ref.embeddedConfluenceSource;
  var initialAllowedFeaturesRef = useRef(undefined);

  // Keep `initialAllowedFeatures` to only contain the initial allowed feature values for cc-frontend SSR rendering
  // Store this value using `useRef` to avoid the iframe url gets updated when `allowedFeatures` React props updated by parent products.
  // If allowedFeatures is equal to 'all' for view mode it must be converted to an array so that 'edit' can be removed.
  var features = useMemo(function () {
    return allowedFeatures === 'all' ? getAllowedFeatures({
      contentType: contentType,
      mode: mode
    }) : allowedFeatures;
  }, [allowedFeatures, contentType, mode]);
  if (!initialAllowedFeaturesRef.current) {
    initialAllowedFeaturesRef.current = Array.isArray(features) ? features.map(function (feature) {
      if (typeof feature !== 'string') {
        return JSON.stringify(feature);
      } else {
        return feature;
      }
    })
    // 'edit' can not be included as an initialAllowedFeature because it requires the navigation policy, which does not work with SSR.
    .filter(function (feature) {
      return feature !== 'edit';
    }).join('.') : features;
  }
  var _useIntl = useIntl(),
    locale = _useIntl.locale;
  var queryParams = useMemo(function () {
    var query = {};
    if (parentProduct) {
      query.parentProduct = parentProduct;
    }
    if (parentProductContentContainerId) {
      query.parentProductContentContainerId = parentProductContentContainerId;
    }
    if (parentProductContentId) {
      query.parentProductContentId = parentProductContentId;
    }
    if (initialAllowedFeaturesRef.current !== undefined) {
      query.initialAllowedFeatures = initialAllowedFeaturesRef.current;
    }
    if (template) {
      query.insertTemplateWithKey = template;
    }
    if (themeState) {
      query.themeState = themeStateObjectToQueryString(themeState);
    }
    if (draftShareId) {
      query.draftShareId = draftShareId;
    }
    if (embeddedConfluenceSource) {
      query.embeddedConfluenceSource = embeddedConfluenceSource;
    }
    if (hostname && hostname !== window.location.host) {
      query.isCrossDomain = true;
    }
    query.locale = locale;
    var isCrossDomain = query.isCrossDomain,
      restQuery = _objectWithoutProperties(query, _excluded);
    return "?".concat(new URLSearchParams(_objectSpread(_objectSpread(_objectSpread({}, isCrossDomain ? {
      isCrossDomain: "".concat(isCrossDomain)
    } : {}), restQuery), contentConfiguration === null || contentConfiguration === void 0 ? void 0 : contentConfiguration.queryParams)).toString());
  }, [contentConfiguration, draftShareId, locale, parentProduct, parentProductContentContainerId, parentProductContentId, themeState, hostname, embeddedConfluenceSource, template]);
  return useMemo(function () {
    return _objectSpread({}, constructUrl(mode, {
      contentId: contentId,
      contentType: contentType,
      hash: hash,
      hostname: hostname,
      protocol: protocol,
      spaceKey: spaceKey,
      queryParams: queryParams
    }));
  }, [contentId, contentType, hash, hostname, mode, protocol, spaceKey, queryParams]);
};
var CONTEXT_PATH = '/wiki/spaces';
var invalidUrls = {
  standaloneConfluenceUrl: null,
  embeddedConfluenceUrl: null
};
function constructUrl() {
  var _contentTypeUrl$conte;
  var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMBEDDED_CONFLUENCE_MODE.VIEW_MODE;
  var _ref2 = arguments.length > 1 ? arguments[1] : undefined,
    contentId = _ref2.contentId,
    contentType = _ref2.contentType,
    _ref2$hash = _ref2.hash,
    hash = _ref2$hash === void 0 ? '' : _ref2$hash,
    hostname = _ref2.hostname,
    protocol = _ref2.protocol,
    spaceKey = _ref2.spaceKey,
    queryParams = _ref2.queryParams;
  if (!contentId) {
    /* eslint-disable-next-line no-console */
    console.error('Content ID is undefined');
    return invalidUrls;
  }
  if (!spaceKey) {
    /* eslint-disable-next-line no-console */
    console.error('Space key is undefined');
    return invalidUrls;
  }
  if (/^\d+$/.test(spaceKey)) {
    /* eslint-disable-next-line no-console */
    console.warn('A numeric "spaceKey" is passed into the Embedded Confluence component. Verify that it is the correct "spaceKey" and not the "spaceId." Disregard this warning if it is correct.');
  }
  var prefix = "".concat(protocol || window.location.protocol, "//").concat(hostname || window.location.host).concat(CONTEXT_PATH);
  var base;
  var pageUrl = _defineProperty(_defineProperty({}, EMBEDDED_CONFLUENCE_MODE.VIEW_MODE, "".concat(spaceKey, "/pages/").concat(contentId)), EMBEDDED_CONFLUENCE_MODE.EDIT_MODE, "".concat(spaceKey, "/pages/edit-embed/").concat(contentId));
  var blogpostUrl = _defineProperty(_defineProperty({}, EMBEDDED_CONFLUENCE_MODE.VIEW_MODE, "".concat(spaceKey, "/pages/").concat(contentId)), EMBEDDED_CONFLUENCE_MODE.EDIT_MODE, "".concat(spaceKey, "/pages/edit-embed/").concat(contentId));
  var whiteboardUrl = _defineProperty(_defineProperty({}, EMBEDDED_CONFLUENCE_MODE.VIEW_MODE, "".concat(spaceKey, "/whiteboard/").concat(contentId)), EMBEDDED_CONFLUENCE_MODE.EDIT_MODE, "".concat(spaceKey, "/whiteboard/").concat(contentId));
  var contentTypeUrl = _defineProperty(_defineProperty(_defineProperty({}, EMBEDDED_CONFLUENCE_CONTENT_TYPE.PAGE, pageUrl), EMBEDDED_CONFLUENCE_CONTENT_TYPE.BLOGPOST, blogpostUrl), EMBEDDED_CONFLUENCE_CONTENT_TYPE.WHITEBOARD, whiteboardUrl);
  base = (_contentTypeUrl$conte = contentTypeUrl[contentType]) === null || _contentTypeUrl$conte === void 0 ? void 0 : _contentTypeUrl$conte[mode];
  if (!base) {
    /* eslint-disable-next-line no-console */
    console.error('URL is not yet supported');
    return invalidUrls;
  }
  var standaloneConfluenceUrl = "".concat(prefix, "/").concat(base).concat(hash);
  var embeddedConfluenceUrl = "".concat(prefix, "/").concat(base).concat(queryParams).concat(hash);
  return {
    standaloneConfluenceUrl: standaloneConfluenceUrl,
    embeddedConfluenceUrl: embeddedConfluenceUrl
  };
}